import React, { useMemo, useState, useEffect } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { Input } from "../../atoms/Input";
import { Button } from "../../atoms/Button";
import { Checkbox } from "../../atoms/Checkbox";
import { ImageUpload } from "../../atoms/ImageUpload";
import FormTemplate from "../../templates/FormTemplate";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";


export type BlogFormData = {
  title: string;
  content: string;
  author_name: string;
  publish: boolean;
  image: File | null;
  delete_image?: boolean;
};

// Extended type for form with existing image URL support
export type BlogFormDataWithExistingImage = {
  title: string;
  content: string;
  author_name: string;
  publish: boolean;
  image: File | null;
  existingImageUrl?: string | null;
};

type Props = {
  handleSubmit: (data: BlogFormData, onError: (message: string) => void) => void | Promise<void>;
  defaultValues?: Partial<BlogFormDataWithExistingImage>;
  mode?: "create" | "edit";
  existingImageUrl?: string | null;
};

export const BlogForm: React.FC<Props> = ({
  handleSubmit,
  defaultValues = {},
  mode = "create",
  existingImageUrl
}) => {
  const [hasTitleError, setHasTitleError] = useState<boolean>(false);
  const [hasContentError, setHasContentError] = useState<boolean>(false);
  const [hasAuthorError, setHasAuthorError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isImageRemoved, setIsImageRemoved] = useState<boolean>(false);

  // Local submission state to prevent multiple clicks
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine the actual existing image URL to pass to ImageUpload
  const actualExistingImageUrl = isImageRemoved ? null : existingImageUrl;

  const handleImageRemove = () => {
    setIsImageRemoved(true); // Mark image as removed
    // Update the form field to null when image is removed
    if (methods) {
      methods.setValue('image', null, { shouldValidate: true });
    }
  };

  const handleImageChange = (file: File | null) => {
    if (file) {
      setIsImageRemoved(false); // Reset removed state when new image is selected
    }
    // Update the form field with the new file
    if (methods) {
      methods.setValue('image', file, { shouldValidate: true });
    }
  };

  const schema = useMemo(() => {
    return z.object({
      title: z.string().min(1, "Title is required"),
      content: z.string().min(1, "Content is required"),
      author_name: z.string().optional(),
      publish: z.boolean(),
      image: z.any().refine((file) => file instanceof File || file === null, {
        message: "Image must be a file",
      }),
    });
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  // Update form field when image removal state changes
  useEffect(() => {
    if (methods && isImageRemoved) {
      methods.setValue('image', null, { shouldValidate: true });
    }
  }, [methods, isImageRemoved]);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      isSubmitting={isSubmitting}
      defaultValues={{
        title: defaultValues.title || "",
        content: defaultValues.content || "",
        author_name: defaultValues.author_name || "",
        publish: defaultValues.publish !== undefined ? defaultValues.publish : true,
        image: isImageRemoved ? null : (defaultValues.image || null),
      }}
      onSubmit={async (data) => {
        // Prevent submission if already submitting
        if (isSubmitting) {
          return;
        }

        setIsSubmitting(true);

        try {
          // Reset errors
          setHasTitleError(false);
          setHasContentError(false);
          setHasAuthorError(false);
          setErrorMessage(undefined);

          // Ensure image is null if explicitly removed
          const finalData = {
            ...data,
            image: isImageRemoved ? null : data.image,
            // Add a flag to indicate image deletion
            delete_image: isImageRemoved ? true : undefined
          };

          // Call handleSubmit and await if it returns a Promise
          const result = handleSubmit(finalData as BlogFormData, (message) => {
            setErrorMessage(message);
            throw new Error(message);
          });

        } catch (error: any) {
          // Error is already handled by setting errorMessage above
          setIsSubmitting(false);
        }
        // Don't reset isSubmitting on success - let the modal close handle it
      }}
    >
      <StyledCheckboxContainer>
        <Checkbox
          name="publish"
          variant="empty"
          label="Published"
          checked={defaultValues.publish !== undefined ? defaultValues.publish : true}
        />
      </StyledCheckboxContainer>

      <StyledFormGrid>
        <StyledInputContainer>
          <Input
            label="Title"
            name="title"
            type="text"
            placeholder="Enter blog title"
            autoFocus
            required
            hasError={hasTitleError}
            errorMessage={hasTitleError ? errorMessage : undefined}
            fullWidth
          />
        </StyledInputContainer>

        <StyledInputContainer>
          <Input
            label="Author Name"
            name="author_name"
            type="text"
            placeholder="Enter author name"
            hasError={hasAuthorError}
            errorMessage={hasAuthorError ? errorMessage : undefined}
            fullWidth
          />
        </StyledInputContainer>
      </StyledFormGrid>

      <StyledImageUpload>
        <ImageUpload
          name="image"
          label="Blog Image"
          required={false}
          fullWidth
          helperText="Upload a cover image for the blog (optional)"
          existingImageUrl={actualExistingImageUrl}
          onImageRemove={handleImageRemove}
          onImageChange={handleImageChange}
        />
      </StyledImageUpload>

      <StyledFormGrid>
        <StyledInputContainer style={{ gridColumn: "1 / -1" }}>
          <QuillLabel>
            Content <span style={{ color: 'red' }}>*</span>
          </QuillLabel>
          <QuillEditorWithHook methods={methods} setHasContentError={setHasContentError} setErrorMessage={setErrorMessage} hasContentError={hasContentError} errorMessage={errorMessage} />
        </StyledInputContainer>
      </StyledFormGrid>

      <StyledFooter>
        <Button
          variant="filled"
          text={isSubmitting ? (mode === "create" ? "Creating..." : "Updating...") : (mode === "create" ? "Create" : "Update")}
          type="submit"
          disabled={false}
          isSubmitting={isSubmitting}
          onClick={() => {
            if (methods) {
              console.log('Blog form button clicked - Form state:', {
                isValid: methods.formState.isValid,
                errors: methods.formState.errors,
                values: methods.getValues(),
                isDirty: methods.formState.isDirty,
                isSubmitting,
                watchedTitle: methods.watch?.('title'),
                watchedContent: methods.watch?.('content')
              });
            }
          }}
        />
      </StyledFooter>
    </FormTemplate>
  );
};

const QuillEditorWithHook: React.FC<{
  methods: any;
  setHasContentError: (v: boolean) => void;
  setErrorMessage: (v: string) => void;
  hasContentError: boolean;
  errorMessage?: string;
}> = ({ methods, setHasContentError, setErrorMessage, hasContentError, errorMessage }) => {
  const { quill, quillRef } = useQuill();

  // Set initial value from form
  useEffect(() => {
    if (quill && methods) {
      const value = methods.getValues('content') || '';
      if (quill.root.innerHTML !== value) {
        quill.root.innerHTML = value;
      }
    }
  }, [quill, methods]);

  // Update form value on text change
  useEffect(() => {
    if (quill && methods) {
      quill.on('text-change', () => {
        methods.setValue('content', quill.root.innerHTML, { shouldValidate: true });
        setHasContentError(false);
      });
    }
  }, [quill, methods, setHasContentError]);

  // Validate on blur
  useEffect(() => {
    if (quill && methods) {
      quill.root.addEventListener('blur', () => {
        if (!quill.root.innerText.trim()) {
          setHasContentError(true);
          setErrorMessage('Content is required');
        }
      });
    }
    // Cleanup
    return () => {
      if (quill && methods) {
        quill.root.removeEventListener('blur', () => { });
      }
    };
  }, [quill, methods, setHasContentError, setErrorMessage]);

  return (
    <>
      <div ref={quillRef} style={{ minHeight: 200, marginBottom: hasContentError ? 0 : 16 }} />
      {hasContentError && (
        <ErrorText>{errorMessage}</ErrorText>
      )}
    </>
  );
};

const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledCheckboxContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center; 
  margin-bottom: 16px;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;

const StyledImageUpload = styled.div`
  margin: 16px 0px 16px 0px;
`;

const QuillLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 8px;
  display: block;
`;

const ErrorText = styled.div`
  color: #e74c3c;
  font-size: 13px;
  margin-top: 4px;
`;